/**
 * DataImport — CSV bulk import for Fitness Assessments + Biometrics
 * -----------------------------------------------------------------
 * Admin-only. Same three-step pattern as the existing BulkImport page:
 *   upload → preview (with per-row errors) → confirm → results
 *
 * Backend endpoints (all Admin-gated):
 *   GET   /api/data-import/{kind}/template
 *   POST  /api/data-import/{kind}/preview
 *   POST  /api/data-import/{kind}/confirm
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Download, ArrowLeft, Loader2, History, Undo2, FileSearch } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const KIND_COPY = {
  assessments: {
    title: 'Import Fitness Assessments',
    description:
      'Bulk-upload fitness assessment results. Rows are grouped by athlete and date — one assessment session will be created or reused per group.',
    columns: [
      { key: 'athlete_name', label: 'Athlete' },
      { key: 'test_date',    label: 'Date' },
      { key: 'test_name',    label: 'Test' },
      { key: 'result_value', label: 'Result' },
      { key: 'result_unit',  label: 'Unit' },
      { key: 'notes',        label: 'Notes' },
    ],
    templateName: 'assessments_template.csv',
  },
  biometrics: {
    title: 'Import Biometrics',
    description:
      'Bulk-upload biometric records (height, weight, BMI, SpO2, etc.). Existing entries on the same date are updated in place.',
    columns: [
      { key: 'athlete_name',         label: 'Athlete' },
      { key: 'date',                 label: 'Date' },
      { key: 'height_cm',            label: 'Height' },
      { key: 'weight_kg',            label: 'Weight' },
      { key: 'bmi',                  label: 'BMI' },
      { key: 'visceral_fat',         label: 'Visceral' },
      { key: 'visceral_fat_unit',    label: 'VF Unit' },
      { key: 'blood_o2_saturation',  label: 'SpO2' },
      { key: 'notes',                label: 'Notes' },
    ],
    templateName: 'biometrics_template.csv',
  },
};

const statusIcon = (status) => {
  if (status === 'valid')   return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-red-500" />;
};
const rowBg = (status) => {
  if (status === 'error')   return 'bg-red-50 dark:bg-red-950/20';
  if (status === 'warning') return 'bg-amber-50 dark:bg-amber-950/20';
  return '';
};

const downloadErrorReportCsv = (rows, kind) => {
  const header = [
    'row_number', 'status', 'errors',
    ...KIND_COPY[kind].columns.map((c) => c.key),
  ];
  const csv = [header.join(',')];
  rows.forEach((r) => {
    const echo = r.echo || {};
    const vals = [
      r.row_number,
      r.status,
      `"${(r.errors || []).join('; ').replace(/"/g, "'")}"`,
      ...KIND_COPY[kind].columns.map((c) => {
        const v = echo[c.key] ?? '';
        return `"${String(v).replace(/"/g, "'")}"`;
      }),
    ];
    csv.push(vals.join(','));
  });
  const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${kind}_import_errors.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const ImportPanel = ({ kind, onCommitted }) => {
  const copy = KIND_COPY[kind];
  const [step, setStep] = useState('upload'); // upload | preview | results
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileInputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/data-import/${kind}/template`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = copy.templateName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Unable to download template');
    }
  };

  const processFile = useCallback(async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await axios.post(
        `${API}/data-import/${kind}/preview`, form,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setPreview(res.data);
      setFile(f);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setUploading(false);
    }
  }, [kind]);

  const onFileSelect = (e) => { processFile(e.target.files?.[0]); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files?.[0]); };

  const reset = () => {
    setStep('upload'); setPreview(null); setResults(null); setFile(null);
  };

  const confirm = async () => {
    if (!file) return;
    setConfirming(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API}/data-import/${kind}/confirm`, form,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setResults(res.data);
      setStep('results');
      toast.success('Import completed');
      if (typeof onCommitted === 'function') onCommitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setConfirming(false);
    }
  };

  const importableCount = preview?.summary?.valid || 0;
  const errorCount      = preview?.summary?.errors || 0;

  return (
    <div className="space-y-4" data-testid={`import-panel-${kind}`}>
      {step === 'upload' && (
        <Card data-testid={`upload-card-${kind}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              {copy.title}
            </CardTitle>
            <CardDescription>{copy.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Button variant="outline" onClick={downloadTemplate} data-testid={`download-template-${kind}`}>
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                dragging ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20'
                         : 'border-gray-300 dark:border-gray-600 hover:border-emerald-300'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid={`dropzone-${kind}`}
            >
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden"
                     onChange={onFileSelect} data-testid={`file-input-${kind}`} />
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Processing CSV…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Upload className="w-10 h-10 text-gray-400" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Drag &amp; drop your CSV here, or click to browse
                  </p>
                  <p className="text-xs text-gray-400">Accepted format: .csv</p>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Athletes are matched by <span className="font-mono">athlete_id</span> when present, otherwise by exact <span className="font-mono">athlete_name</span>. Athletes are never auto-created.
            </p>
          </CardContent>
        </Card>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="ghost" onClick={reset} data-testid={`back-to-upload-${kind}`}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex gap-3 text-sm">
                <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> {preview.summary.valid} valid</span>
                <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-4 h-4" /> {preview.summary.warnings} warnings</span>
                <span className="flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" /> {errorCount} errors</span>
              </div>
              <Button onClick={confirm} disabled={confirming || importableCount === 0}
                      data-testid={`confirm-${kind}`}>
                {confirming
                  ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>)
                  : `Import ${importableCount} row${importableCount !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>

          {errorCount > 0 && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300"
                 data-testid={`error-banner-${kind}`}>
              {errorCount} row(s) have errors and will be skipped. Fix them in your CSV and re-upload to include them.
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                      <TableHead className="w-10">#</TableHead>
                      <TableHead className="w-10">Status</TableHead>
                      {copy.columns.map((c) => (
                        <TableHead key={c.key}>{c.label}</TableHead>
                      ))}
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((r) => (
                      <TableRow key={r.row_number} className={rowBg(r.status)}
                                data-testid={`row-${kind}-${r.row_number}`}>
                        <TableCell className="text-xs text-gray-400">{r.row_number}</TableCell>
                        <TableCell>{statusIcon(r.status)}</TableCell>
                        {copy.columns.map((c) => (
                          <TableCell key={c.key} className="text-sm">
                            {r.echo?.[c.key] || <span className="text-gray-300">—</span>}
                          </TableCell>
                        ))}
                        <TableCell>
                          <div className="space-y-0.5">
                            {(r.errors || []).map((e, i) => (
                              <p key={`e${i}`} className="text-xs text-red-600 dark:text-red-400">{e}</p>
                            ))}
                            {(r.warnings || []).map((w, i) => (
                              <p key={`w${i}`} className="text-xs text-amber-600 dark:text-amber-400">{w}</p>
                            ))}
                            {!(r.errors?.length) && !(r.warnings?.length) && (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 'results' && results && (
        <div className="space-y-4" data-testid={`results-${kind}`}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Import Complete
              </CardTitle>
              <CardDescription>Summary of {kind} import</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-700" data-testid={`created-count-${kind}`}>
                    {results.committed?.created_count || 0}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">Created</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700" data-testid={`updated-count-${kind}`}>
                    {results.committed?.updated_count || 0}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">Updated</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-700" data-testid={`skipped-count-${kind}`}>
                    {results.summary?.errors || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Skipped (errors)</p>
                </div>
                <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-700" data-testid={`write-errors-${kind}`}>
                    {results.committed?.error_count || 0}
                  </p>
                  <p className="text-xs text-red-600 mt-1">Write errors</p>
                </div>
              </div>

              {results.committed?.import_batch_id && (
                <p className="text-xs text-gray-500 mb-4">
                  Batch ID: <span className="font-mono">{results.committed.import_batch_id}</span>
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-4 border-t dark:border-gray-700">
                <Button onClick={reset} data-testid={`import-another-${kind}`}>
                  <Upload className="w-4 h-4 mr-2" /> Import Another CSV
                </Button>
                {(results.summary?.errors || 0) > 0 && (
                  <Button variant="outline"
                          onClick={() => downloadErrorReportCsv(results.preview_rows || [], kind)}
                          data-testid={`download-error-report-${kind}`}>
                    <Download className="w-4 h-4 mr-2" /> Download Error Report
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const ImportHistory = ({ version, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // batch awaiting confirm
  const [undoing, setUndoing] = useState(false);
  const [summary, setSummary] = useState(null); // post-undo summary banner

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/data-import/history`,
                                  { headers: authHeaders() });
      setRows(res.data.batches || []);
    } catch (err) {
      toast.error('Unable to load import history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory, version]);

  const confirmUndo = async () => {
    if (!pending) return;
    setUndoing(true);
    try {
      const res = await axios.post(
        `${API}/data-import/batches/${pending.id}/undo`,
        {},
        { headers: authHeaders() },
      );
      const d = res.data;
      setSummary({ id: pending.id, ...d });
      toast.success(
        `Undo complete — ${d.removed} removed, ${d.restored} restored, ${d.skipped?.length || 0} skipped`
      );
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    } finally {
      setUndoing(false);
      setPending(null);
    }
  };

  return (
    <Card data-testid="import-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="w-5 h-5 text-gray-500" /> Import History
        </CardTitle>
        <CardDescription>
          Recent CSV imports. Use Undo to safely roll back a batch by <span className="font-mono">import_batch_id</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 sm:p-6 sm:pt-0">
        {summary && (
          <div className="m-4 sm:m-0 sm:mb-4 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 p-3 text-sm"
               data-testid="undo-summary">
            <p className="text-emerald-700 dark:text-emerald-300 font-medium">
              Undo complete for batch <span className="font-mono">{summary.id.slice(0, 8)}…</span>
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">
              Removed: <strong>{summary.removed}</strong> · Restored: <strong>{summary.restored}</strong> · Skipped: <strong>{summary.skipped?.length || 0}</strong>
              {summary.errors?.length ? <> · Errors: <strong>{summary.errors.length}</strong></> : null}
            </p>
            {summary.skipped?.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-emerald-800 dark:text-emerald-300 cursor-pointer">
                  Show skipped rows ({summary.skipped.length})
                </summary>
                <ul className="mt-1 text-xs text-emerald-800 dark:text-emerald-400 list-disc ml-5 space-y-0.5">
                  {summary.skipped.map((s, i) => (
                    <li key={i}>
                      <span className="font-mono">{(s.id || '').slice(0, 8)}…</span> — {s.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            No imports recorded yet.
          </p>
        ) : (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                  <TableHead>Batch</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="text-right">Created</TableHead>
                  <TableHead className="text-right">Updated</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((b) => {
                  const isUndone = b.status === 'undone';
                  const when = b.imported_at
                    ? new Date(b.imported_at).toLocaleString()
                    : '—';
                  return (
                    <TableRow key={b.id} data-testid={`history-row-${b.id}`}>
                      <TableCell className="font-mono text-xs text-gray-500">
                        {b.id.slice(0, 8)}…
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {b.kind}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {b.imported_by_name || <span className="text-gray-400 italic">unknown</span>}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{when}</TableCell>
                      <TableCell className="text-right text-sm">{b.created_count || 0}</TableCell>
                      <TableCell className="text-right text-sm">{b.updated_count || 0}</TableCell>
                      <TableCell>
                        {isUndone ? (
                          <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 text-[10px]">
                            Undone
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-[10px]">
                            Committed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/system-logs?module=data_import&target_id=${b.id}`}
                            className="text-xs text-gray-600 hover:text-emerald-600 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                            data-testid={`audit-trail-link-${b.id}`}
                            title="View related audit entries in System Logs"
                          >
                            <FileSearch className="w-3.5 h-3.5" /> View audit trail
                          </Link>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isUndone}
                            onClick={() => setPending(b)}
                            data-testid={`undo-btn-${b.id}`}
                          >
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent data-testid="undo-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this import?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to undo this import? This will remove or
              revert records created or updated by this batch.
              {pending && (
                <>
                  <br /><br />
                  <span className="block text-xs text-gray-500">
                    Batch: <span className="font-mono">{pending.id}</span><br />
                    Type: <span className="capitalize">{pending.kind}</span><br />
                    Created {pending.created_count}, Updated {pending.updated_count}.
                  </span>
                  <span className="block text-xs text-amber-600 mt-2">
                    Records that were edited after the import will be skipped (never silently overwritten).
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="undo-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUndo}
              disabled={undoing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="undo-confirm-btn"
            >
              {undoing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Undoing…</>) : 'Yes, undo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const DataImport = () => {
  // History reloads when an import completes or an undo runs.
  const [historyVersion, setHistoryVersion] = useState(0);
  const bumpHistory = useCallback(() => setHistoryVersion((v) => v + 1), []);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="data-import-page">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
            Data Import
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">
            Bulk import athlete assessments and biometrics from CSV
          </p>
        </div>

        <Tabs defaultValue="assessments" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="assessments" data-testid="tab-assessments">
              Fitness Assessments
            </TabsTrigger>
            <TabsTrigger value="biometrics" data-testid="tab-biometrics">
              Biometrics
            </TabsTrigger>
          </TabsList>
          <TabsContent value="assessments">
            <ImportPanel kind="assessments" onCommitted={bumpHistory} />
          </TabsContent>
          <TabsContent value="biometrics">
            <ImportPanel kind="biometrics" onCommitted={bumpHistory} />
          </TabsContent>
        </Tabs>

        <div className="mt-8">
          <ImportHistory version={historyVersion} onChanged={bumpHistory} />
        </div>
      </div>
    </Layout>
  );
};

export default DataImport;
